import { useEffect, useState, useCallback, useRef } from 'react';
import { Amplify } from 'aws-amplify';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faSignOutAlt, faCheck } from '@fortawesome/free-solid-svg-icons';
import Icon from '../ui-components/Icon/Icon';
import Button from '../ui-components/Button/Button';
import SideBar from '../ui-components/SideBar/SideBar';
import "../../src/styles/header.css";
import BRAND_LOGO from '../images/Shogun-white-logo.png';
import SAMURAI_LOGO from '../images/Capa_2.png';
import { businessTokenMapping } from '../redux/actions/businessTokenMappingActions';
import { fetchUser } from '../redux/actions/fetchUser';
import awsconfig from '../aws-exports';
import RoninLeftSide from '../images/RoninLeftSide.png';
import REID from '../images/reid.svg';

Amplify.configure(awsconfig);

// Updated CustomModal component with proper click-outside detection
const CustomModal = ({ show, onHide, children, className = '', enableClickOutside = false }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!show || !enableClickOutside) return;

    const handleClickOutside = (event) => {
      // Check if click is on the modal background (not the modal content)
      if (modalRef.current && event.target === modalRef.current) {
        onHide();
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, enableClickOutside, onHide]);

  if (!show) return null;
  
  return (
    <div 
      ref={modalRef}
      className={`isolated-modal-container ${className}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 2000,
        display: 'flex',
        alignItems: className.includes('logout-warning') ? 'center' : 'flex-start',
        justifyContent: className.includes('logout-warning') ? 'center' : className.includes('user-profile') ? 'flex-end' : 'center',
        paddingTop: className.includes('user-profile') ? '70px' : '0',
        paddingRight: className.includes('user-profile') ? '20px' : '0',
        pointerEvents: 'auto'
      }}
    >
      <div className="modal-box" style={{ pointerEvents: 'auto' }}>
        {children}
      </div>
    </div>
  );
};

const CustomModalHeader = ({ children, onClose }) => (
  <div className="isolated-modal-header">
    <div className="isolated-modal-title-container">
      {children}
      <button className="isolated-custom-close" onClick={onClose}>×</button>
    </div>
  </div>
);

const CustomModalBody = ({ children, className = '' }) => (
  <div className={`isolated-modal-body ${className}`}>{children}</div>
);

const MyNavbar = ({ isSmallScreen }) => {
    const [user, setUser] = useState(localStorage.getItem('email'));
    const [showLogout, setShowLogout] = useState(false);
    const [showLogoutWarningModal, setShowLogoutWarningModal] = useState(false);
    const [copiedStates, setCopiedStates] = useState({}); // Track copy state per token
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

    // Refs for click-outside detection
    const hamburgerMenuRef = useRef(null);
    
    const businessTokenMappingData = useSelector(state => state.businessTokenMapping || []);
    const userFirstLetter = user?.charAt(0)?.toUpperCase();
    
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    // Determine if current route should show QuickMenu
    const routesWithQuickMenu = ['/analytic', '/access', '/reporting', '/csv/download', '/csv/scheduler', '/cloud/scheduler', '/cloud/transfer-history'];
    const shouldShowQuickMenu = routesWithQuickMenu.some(route => location.pathname.startsWith(route));

    // Get user role for QuickMenu
    let userRole = localStorage.getItem('rbac_role')?.toLowerCase();
    if (!userRole || userRole === 'null') {
        try {
            const businessRoles = JSON.parse(localStorage.getItem('rbac_data')) || [];
            const rolePriority = ['technician', 'supervisor', 'admin', 'tla'];
            for (let i = rolePriority.length - 1; i >= 0; i--) {
                const match = businessRoles.find(b => b.role.toLowerCase() === rolePriority[i]);
                if (match) {
                    userRole = rolePriority[i];
                    localStorage.setItem('rbac_role', userRole);
                    break;
                }
            }
        } catch (e) {
            console.error('Error parsing rbac_data:', e);
            userRole = null;
        }
    }

    // QuickMenu items - converted for SideBar component
    const menuItems = [
        {
            id: 'base-suite',
            label: 'Base Suite',
            icon: <Icon name="analytics" size="40px" />,
            path: '/analytic?view=baseSuite',
            allowedRoles: ['admin', 'supervisor', 'technician', 'tla']
        },
        {
            id: 'lpr',
            label: 'LPR',
            icon: <Icon name="lpr" size="40px" />,
            path: '/analytic?view=lprBaseSuite',
            allowedRoles: ['admin', 'supervisor', 'technician', 'tla']
        },
        {
            id: 'infringement',
            label: 'Infringement',
            icon: <Icon name="infringement" size="40px" />,
            path: '/analytic?view=lprInfringement',
            allowedRoles: ['admin', 'supervisor', 'technician', 'tla']
        },
        {
            id: 'lpr-journey',
            label: 'LPR Journey',
            icon: <Icon name="lpr-journey" size="40px" />,
            path: '/analytic?view=lprJourney',
            allowedRoles: ['admin', 'supervisor', 'technician', 'tla']
        },
        {
          id:'reid',
          label: 'RE-ID',
          icon: <img src={REID} alt="RE-ID" name="reid" className="ui-icon" style={{ filter: 'brightness(0) invert(1)' }} />,
          path: '/analytic?view=reid',
          allowedRoles: ['admin', 'supervisor', 'technician', 'tla']
        },
        {
            id: 'reporting',
            label: 'Reporting',
            icon: <Icon name="reporting" size="40px" />,
            path: '/reporting',
            allowedRoles: ['admin', 'tla']
        },
        {
            id: 'access',
            label: 'Access Control',
            icon: <Icon name="access-control" size="40px" />,
            path: '/access',
            allowedRoles: ['admin', 'supervisor', 'tla']
        }
    ];

    // Click-outside functionality for hamburger menu
    useEffect(() => {
        if (!isSmallScreen) return;

        const handleClickOutside = (event) => {
            const hamburgerToggle = document.getElementById('toggle1');
            
            // Only proceed if menu is open
            if (!hamburgerToggle?.checked) return;
            
            // If click is outside the hamburger menu container, close it
            if (hamburgerMenuRef.current && !hamburgerMenuRef.current.contains(event.target)) {
                hamburgerToggle.checked = false;
            }
        };

        // Add event listener
        document.addEventListener('click', handleClickOutside);
        
        // Cleanup
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isSmallScreen]);


    // Your existing functions with improvements...
    const handleCloseLogout = () => {
        setShowLogout(false);
        setCopiedStates({}); // Clear all copy states when modal closes
    }

    const handleShowLogout = () => {  
        setShowLogout(true);    
    }

    const handleCloseLogoutWarning = () => {
        setShowLogoutWarningModal(false);
    }

    const handleShowLogoutWarning = () => {
        setShowLogoutWarningModal(true);  
    }

    const logout = useCallback(() => {
        setShowLogout(false);
        setShowLogoutWarningModal(false);
        localStorage.clear();
        navigate('/');  
        setUser('');  
    }, [navigate]);

    // Enhanced copy functionality with better user feedback
    const handleCopyText = async (e, text, tokenId) => {
        e.stopPropagation();
        
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                // Fallback for older browsers
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.left = '-999999px';
                textarea.style.top = '-999999px';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                
                const successful = document.execCommand('copy');
                document.body.removeChild(textarea);
                
                if (!successful) {
                    throw new Error('Copy command failed');
                }
            }
            
            // Set copied state for this specific token
            setCopiedStates(prev => ({...prev, [tokenId]: true}));
            
            // Clear the copied state after 2 seconds
            setTimeout(() => {
                setCopiedStates(prev => ({...prev, [tokenId]: false}));
            }, 2000);
            
        } catch (err) {
            console.error('Copy failed:', err);
            
            // Show error feedback
            setCopiedStates(prev => ({...prev, [tokenId]: 'error'}));
            setTimeout(() => {
                setCopiedStates(prev => ({...prev, [tokenId]: false}));
            }, 2000);
        }
    };

    // Prepare footer content for SideBar component
    const sidebarLogo = SAMURAI_LOGO;

    const sidebarFooterContent = <Icon name="samurai-analytics" size="55px" />;

    const cardData = [
        {
            id: 1,
            image: RoninLeftSide,
            text: 'Real-time diagnostics, proactive alerts, and centralised insights to prevent downtime and optimise performance.'
        }
    ];

    // Get copy button state and styling
    const getCopyButtonState = (tokenId) => {
        const state = copiedStates[tokenId];
        if (state === true) return { icon: faCheck, color: '#22c55e', text: 'Copied!' };
        if (state === 'error') return { icon: faCopy, color: '#ef4444', text: 'Failed' };
        return { icon: faCopy, color: '#ffffffff' };
    };



    useEffect(() => {
        if (user) dispatch(businessTokenMapping({user: user}));
    }, [user, dispatch]);

    useEffect(() => {
        setUser(localStorage.getItem('email'));
    }, [navigate]);

    useEffect(() => {
        dispatch(fetchUser(user));
    }, [user, dispatch]);

    return (
        <>

            {shouldShowQuickMenu && !isSmallScreen && (
                <SideBar
                    menuItems={menuItems}
                    logo={sidebarLogo}
                    cardData={cardData}
                    footerContent={sidebarFooterContent}
                    defaultCollapsed={true}
                    webUrl='https://www.artoflogic.ai/r/v4k'
                    userRole={userRole}
                    onCollapseChange={setIsSidebarCollapsed}
                />
            )}

                        {/* Main Navigation */}
            <div className="nav-container">
                {isSmallScreen ? (
                    <div>
                        <img src={BRAND_LOGO} alt='' className='nav-container-img'/>
                    </div>
                ) : null}
                
                {!isSmallScreen ? (
                    <div>
                        <div className={`top-left-image-container ${shouldShowQuickMenu && !isSidebarCollapsed ? 'sidebar-expanded' : ''}`}>
                            <img
                                src={BRAND_LOGO}
                                alt="Logo"
                                className="top-left-image"
                            />
                        </div>
                        
                        <ul className='header-ul'>
                            {user ? (
                                <>
                                    <li key="avatar" style={{ marginTop: '1rem', marginRight: '1rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>                
                                            <div style={{ marginRight: '0.9rem', color: 'darkgray'}}>                  
                                                <Button variant='blank' className='avatar' name='blank' size='large' onClick={() => handleShowLogout()}>{userFirstLetter}</Button>
                                            </div>
                                        </div>
                                    </li>
                                </>
                            ) : null}
                        </ul>
                    </div>
                ) : (
                    <div style={{display: 'flex'}}>
                        <section className="p-menu1" ref={hamburgerMenuRef}>
                            <nav className="navigation" role="navigation">
                                <input id="toggle1" type="checkbox" />
                                <label className="hamburger1" htmlFor="toggle1">
                                    <div className="top"></div>
                                    <div className="meat"></div>
                                    <div className="bottom"></div>
                                </label>
                                <nav className="menu1">
                                    {user ? (          
                                        <Button
                                            className="link1"
                                            style={{ borderRadius: 0, margin: '0' }}
                                            type='text'
                                            variant='danger'
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const hamburgerToggle = document.getElementById('toggle1');
                                                if (hamburgerToggle) {
                                                    hamburgerToggle.checked = false;
                                                }
                                                handleShowLogoutWarning();
                                            }}
                                        >
                                            Logout
                                        </Button>
                                    ) : (                
                                        <a className="link1" href='/#auth/signin'>Login</a>                    
                                    )}
                                </nav>
                            </nav>
                        </section>
                    </div>
                )}

                {/* Simplified User Profile Modal with Copy-Only Functionality */}
                <CustomModal 
                    show={showLogout} 
                    onHide={handleCloseLogout}
                    className="user-profile-modal-isolated"
                    enableClickOutside={true}
                >
                    <CustomModalHeader onClose={handleCloseLogout}>
                        <div>{user}</div>
                    </CustomModalHeader>
                    
                    <CustomModalBody>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            <div className='avatar-modal'>{userFirstLetter}</div>
                        </div>
                     
                        <div className='business-token-table-container'>              
                            <table>
                                <thead>
                                    <tr>
                                        <th className='business-token-table-header'>
                                            Business
                                        </th>
                                        <th className='business-token-table-header' style={{textAlign: 'center'}}>
                                            Token                 
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                {
                                    businessTokenMappingData.map((data, index) => (
                                        data && (
                                        <tr 
                                            key={`${data.business}-${index}`}
                                            className='business-token-row-simple'
                                        >
                                            <td className='business-token-table-column business-name-column-simple'>
                                                <div className="business-name-simple">
                                                    {data?.business}
                                                </div>
                                            </td>
                                            <td className='business-token-table-column token-column-simple'>
                                                <Button style={{ border: 'none' }}
                                                    className={`copy-token-btn ${copiedStates[`${data.business}-${index}`] === true ? 'copied' : copiedStates[`${data.business}-${index}`] === 'error' ? 'error' : ''}`}
                                                    title={getCopyButtonState(`${data.business}-${index}`).text}
                                                    onClick={(e) => handleCopyText(e, data.token, `${data.business}-${index}`)}
                                                    aria-label={`Copy ${data.business}`}
                                                >
                                                    <FontAwesomeIcon 
                                                        icon={getCopyButtonState(`${data.business}-${index}`).icon}
                                                        color={getCopyButtonState(`${data.business}-${index}`).color}
                                                    />

                                                </Button>
                                            </td>
                                        </tr>
                                        )
                                    ))
                                }
                                </tbody>
                            </table>
                            {businessTokenMappingData.length === 0 && (
                                <div className="no-tokens-message">
                                    <p>No tokens available</p>
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center'}}>
                            <Button variant="ghost" 
                                onClick={handleShowLogoutWarning} 
                            >
                                <FontAwesomeIcon icon={faSignOutAlt} />
                                <span className='signout-btn-text'>Sign out</span>
                            </Button>
                        </div>            
                    </CustomModalBody>
                </CustomModal>
     
                {/* Custom Logout Warning Modal */}
                <CustomModal 
                    show={showLogoutWarningModal} 
                    onHide={handleCloseLogoutWarning}
                    className="logout-warning-modal-isolated"
                    enableClickOutside={true}
                >
                    <CustomModalHeader onClose={handleCloseLogoutWarning}>
                        <div></div>
                    </CustomModalHeader>
                    
                    <CustomModalBody className="logout-warning-modal-body">
                        <div className='logout-warning-text'>                
                            Are you sure you want to sign out?
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center'}}>
                            <Button
                                variant='ghost'
                                size='sm'
                                onClick={logout} 
                            >                    
                                Sign out
                            </Button>
                        </div>            
                    </CustomModalBody>
                </CustomModal>
            </div>
        </>
    );
};

export default MyNavbar;